import { UntilDestroy } from '@ngneat/until-destroy';
import { Directive, computed, inject, input } from '@angular/core';
import {
  ImmomioIconName,
  EnhancedContract,
  ObjectInfoField,
  IObjectInfo,
  ObjectInfoKey,
} from '../../models';
import { DisplayCurrencyPipe } from '../../pipes/display-currency.pipe';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Directive()
export abstract class ObjectInfoBaseComponent {
  contract = input<EnhancedContract | null>(null);
  public collapsed = true;
  objectInfoFields: ObjectInfoField[] = [
    { key: 'paidDeposit', label: 'contract.object_info.deposit' },
    { key: 'floor', label: 'contract.object_info.floor' },
    { key: 'rooms', label: 'contract.object_info.rooms' },
    { key: 'halfRooms', label: 'contract.object_info.half_rooms' },
    { key: 'size', label: 'contract.object_info.living_space' },
  ];

  objectInfo = computed<IObjectInfo>(() => {
    const contract = this.contract();
    const property = contract?.property;

    return {
      floor: property?.floor,
      rooms: property?.rooms,
      halfRooms: property?.halfRooms,
      size: property?.size,
      expectedDeposit: contract?.expectedDeposit,
      paidDeposit: contract?.paidDeposit,
    };
  });

  visibleObjectInfoFields = computed<ObjectInfoField[]>(() => {
    const info = this.objectInfo();

    return this.objectInfoFields.filter(field => this.hasFieldValue(field.key, info));
  });

  protected readonly ImmomioIconName = ImmomioIconName;
  private displayCurrencyPipe = new DisplayCurrencyPipe('de-DE', 'EUR');
  private translate = inject(TranslateService);

  getFieldValue(field: ObjectInfoKey): string {
    if (field === 'paidDeposit') {
      const info = this.objectInfo();
      const paid = info.paidDeposit;
      const expected = info.expectedDeposit;

      if (paid == null && expected == null) {
        return '-';
      }

      if (paid != null && expected != null) {
        return `${this.displayCurrencyPipe.transform(paid)} (${this.translate.instant('contract.object_info.deposit_awaiting')} ${this.displayCurrencyPipe.transform(expected)})`;
      }

      if (paid != null) {
        return this.displayCurrencyPipe.transform(paid);
      }
      return `${this.translate.instant('contract.object_info.deposit_awaiting')} ${this.displayCurrencyPipe.transform(expected)}`;
    }

    const value = this.objectInfo()[field];

    if (value === undefined || value === null) {
      return '-';
    }

    return value.toString();
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
  }

  private hasFieldValue(field: ObjectInfoKey, info: IObjectInfo): boolean {
    if (field === 'paidDeposit') {
      const paid = info.paidDeposit;
      const expected = info.expectedDeposit;

      // Only hide the deposit field when both values are missing
      return paid != null || expected != null;
    }

    const value = info[field];
    return value !== undefined && value !== null;
  }
}
